import React, { useEffect, useState } from 'react';
import { getSites, addSite, deleteSite, pingSite, updateSite, getUptime, deployAgents, getAgentVersion,
         scanDropins, fixAllDropins, fixSiteDropins } from '../api';
import { Globe, Plus, Trash2, RefreshCw, Pencil, Check, X, SlidersHorizontal, ChevronDown, Download,
         Stethoscope, Wrench, AlertTriangle, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/Button';
import Flag from '../components/Flag';

const COUNTRIES = [
  'USA','UK','Canada','Australia','Germany','France',
  'UAE','Pakistan','India','Netherlands','Singapore','Other'
];

// Optional table columns (Site + Actions are always visible)
const ALL_COLS = [
  { key: 'country',  label: 'Country' },
  { key: 'url',      label: 'URL' },
  { key: 'status',   label: 'Status' },
  { key: 'uptime',   label: 'Uptime (7d)' },
  { key: 'agent',    label: 'Agent' },
  { key: 'wp',       label: 'WordPress' },
  { key: 'php',      label: 'PHP' },
  { key: 'response', label: 'Response' },
];
const COLS_STORAGE_KEY = 'wpm_sites_cols';

/* Tiny uptime tick-strip: one tick per health check (last 30). Uses the
   app's reserved status colors; offline ticks are taller so state is not
   conveyed by color alone, and the % figure sits beside it in plain ink. */
function UptimeCell({ stat, series }) {
  if (!stat || !stat.total) {
    return <span style={{ fontSize: 11, color: 'var(--text3)' }}>No data yet</span>;
  }
  const pct = (stat.up / stat.total) * 100;
  const label = pct >= 99.95 ? '100%' : `${pct.toFixed(1)}%`;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', minWidth: 42 }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 14 }} aria-hidden="true">
        {(series || []).map((c, i) => (
          <span key={i}
            title={`${c.ok ? 'Online' : 'Offline'}${c.ms ? ` · ${c.ms}ms` : ''} · ${new Date(c.t).toLocaleString()}`}
            style={{
              width: 3,
              height: c.ok ? 9 : 14,
              borderRadius: 1,
              background: c.ok ? 'var(--green)' : 'var(--red)',
              opacity: c.ok ? .85 : 1,
            }} />
        ))}
      </div>
    </div>
  );
}


/* Which agent version a site runs vs. the latest bundled with the dashboard.
   'unknown' = the site hasn't reported a version yet (old v1 agent, or not
   pinged since the update) — those are the ones still needing the update. */
function AgentBadge({ version, latest }) {
  if (!version) return <span className="badge gray" title="No version reported — old agent or not pinged yet">unknown</span>;
  if (latest && version === latest) return <span className="badge green" title="Latest agent">v{version}</span>;
  return <span className="badge amber" title={`Latest is v${latest || '?'}`}>v{version} · old</span>;
}

export default function Sites() {
  const [sites, setSites]       = useState([]);
  const [form, setForm]         = useState({ name:'', url:'', token:'', country:'USA' });
  const [adding, setAdding]     = useState(false);
  const [pinging, setPinging]   = useState({});
  const [editing, setEditing]   = useState(null); // {id, name, country}
  const [uptime, setUptime]     = useState({ stats: {}, series: {} });
  const [latestAgent, setLatestAgent] = useState(null);
  const [scan, setScan]         = useState(null);  // cache drop-in scan results
  const [scanning, setScanning] = useState(false);
  const [fixing, setFixing]     = useState({});    // site_id | 'all' -> bool
  const [colsOpen, setColsOpen] = useState(false);
  const [cols, setCols] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(COLS_STORAGE_KEY));
      if (Array.isArray(saved) && saved.length) return new Set(saved);
    } catch { /* fall through to default */ }
    return new Set(ALL_COLS.map(c => c.key)); // default: everything visible
  });

  const show = k => cols.has(k);
  const toggleCol = (key) => {
    setCols(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  const load = () => getSites().then(setSites).catch(console.error);
  const loadUptime = () => getUptime(168).then(r => {
    const stats = {};
    (r.stats || []).forEach(s => { stats[s.site_id] = s; });
    setUptime({ stats, series: r.series || {} });
  }).catch(() => {});

  useEffect(() => {
    load();
    loadUptime();
    getAgentVersion().then(r => setLatestAgent(r.version || null)).catch(() => {});
  }, []);

  // Push the bundled agent to every site, replacing it in place — sites
  // stay connected (same folder + token), nothing to re-connect.
  const handleUpdateAgents = async () => {
    if (!window.confirm(`Update the WP Manager Agent plugin on all ${sites.length} site(s)?\n\nThe agent is replaced in place — sites stay connected, tokens are untouched, no re-connect needed. This can take a few minutes.`)) return;
    try {
      const r = await deployAgents();
      const ok = r.results.filter(x => x.success).length;
      const fail = r.results.length - ok;
      if (fail) {
        console.warn('Agent update failures:', r.results.filter(x => !x.success));
        toast.error(`Agent v${r.version}: ${ok}/${r.results.length} updated — ${fail} failed (see console)`, { duration: 8000 });
      } else {
        toast.success(`Agent v${r.version} installed on all ${ok} site(s)!`, { duration: 6000 });
      }
    } catch (err) {
      toast.error(err?.error || 'Agent update failed');
    }
    load(); // refresh the Agent column
  };

  // Find sites carrying a broken cache drop-in — an object-cache.php left
  // behind by a removed cache plugin prints an error in front of every
  // response, which is what leaves a site's plugin list empty here.
  const handleScanDropins = async () => {
    setScanning(true);
    try {
      const r = await scanDropins();
      setScan(r);
      if (r.affected) toast.error(`${r.affected} of ${r.scanned} site(s) have a broken cache drop-in`, { duration: 7000 });
      else toast.success(`No broken cache drop-ins on ${r.scanned} site(s)`);
    } catch (err) {
      toast.error(err?.error || 'Drop-in scan failed');
    } finally { setScanning(false); }
  };

  // Remove the broken files. Each one is renamed to a timestamped backup
  // beside itself on the site, so nothing is destroyed.
  const runFix = async (key, call, label) => {
    setFixing(f => ({ ...f, [key]: true }));
    try {
      const r = await call();
      // Single-site calls answer with one site result; the bulk call wraps many.
      const perSite = r.site_id !== undefined ? [r] : (r.results || []);
      const removed = perSite.reduce((n, x) => n + (x.removed?.length || 0), 0);
      const failed  = perSite.filter(x => x.success === false);
      if (removed) toast.success(`${label}: removed ${removed} drop-in file(s) — sites re-synced`, { duration: 7000 });
      if (failed.length) toast.error(`${failed.length} site(s) could not be fixed: ${failed.map(f => f.error).filter(Boolean).join(' · ') || 'file not writable'}`, { duration: 10000 });
      else if (!removed) toast(`${label}: nothing to remove`);
      await handleScanDropins();
      load();
    } catch (err) {
      toast.error(err?.error || 'Fix failed');
    } finally { setFixing(f => ({ ...f, [key]: false })); }
  };

  const affected = (scan?.results || []).filter(r => r.needs_fix);

  const handleAdd = async e => {
    e.preventDefault();
    if (!form.country || form.country === '') {
      toast.error('Please select a country');
      return;
    }
    setAdding(true);
    try {
      await addSite(form);
      toast.success('Site connected!');
      setForm({ name:'', url:'', token:'', country:'USA' });
      load();
    } catch(err) { toast.error(err?.error || 'Failed to connect site'); }
    finally { setAdding(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Remove this site?')) return;
    await deleteSite(id); load(); toast.success('Site removed');
  };

  const handlePing = async id => {
    setPinging(p => ({...p,[id]:true}));
    try {
      const r = await pingSite(id); load();
      toast.success(r.status === 'online' ? '✓ Online' : '✗ Offline');
    } catch { toast.error('Ping failed'); }
    finally { setPinging(p => ({...p,[id]:false})); }
  };

  const handleEditSave = async () => {
    try {
      await updateSite(editing.id, { name: editing.name, country: editing.country });
      toast.success('Updated!');
      setEditing(null);
      load();
    } catch { toast.error('Update failed'); }
  };

  const dotColor = s => s==='online'?'var(--green)':s==='offline'?'var(--red)':'var(--amber)';

  return (
    <>
      <div className="page-header">
        <div><h1>My Sites</h1><p>{sites.length} site{sites.length!==1?'s':''} connected</p></div>
        <div className="header-actions">
          <Button className="btn" onClick={handleScanDropins} loading={scanning} disabled={!sites.length}>
            <Stethoscope size={13}/> {scanning ? 'Scanning...' : 'Check cache drop-ins'}
          </Button>
          <Button className="btn primary" onClick={handleUpdateAgents} disabled={!sites.length}>
            <Download size={13}/> Update agents on all sites
          </Button>
        </div>
      </div>
      <div className="page-content">

        {/* Add form */}
        <div className="card">
          <div className="card-head"><h3><Plus size={14}/> Connect new site</h3></div>
          <div style={{padding:'12px 16px'}}>
            <div style={{background:'var(--accent-bg)',border:'1px solid var(--accent2)',borderRadius:8,padding:'10px 12px',fontSize:11,color:'var(--accent-text)',marginBottom:12}}>
              Install <strong>WP Manager Agent</strong> plugin on WordPress site → Settings → WP Manager → copy token.
            </div>
            <form onSubmit={handleAdd} style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 170px auto',gap:8,alignItems:'end'}}>
              <div className="field" style={{margin:0}}>
                <label>Site name</label>
                <input type="text" placeholder="My Store" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/>
              </div>
              <div className="field" style={{margin:0}}>
                <label>Site URL *</label>
                <input type="url" placeholder="https://mysite.com" value={form.url} onChange={e=>setForm({...form,url:e.target.value})} required/>
              </div>
              <div className="field" style={{margin:0}}>
                <label>Secret token *</label>
                <input type="text" placeholder="Paste token here" value={form.token} onChange={e=>setForm({...form,token:e.target.value})} required/>
              </div>
              <div className="field" style={{margin:0}}>
                <label>Country *</label>
                <select value={form.country} onChange={e=>setForm({...form,country:e.target.value})} required>
                  {COUNTRIES.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <Button className="btn primary" loading={adding} type="submit">
                <Plus size={13}/> {adding?'Connecting...':'Connect'}
              </Button>
            </form>
          </div>
        </div>

        {/* Cache drop-in health — only after a scan has run */}
        {scan && (
          <div className="card">
            <div className="card-head">
              <h3>
                {affected.length ? <AlertTriangle size={14}/> : <CheckCircle2 size={14}/>} Cache drop-ins
                {affected.length > 0 && <span className="seg-count">{affected.length}</span>}
              </h3>
              {affected.length > 1 && (
                <Button className="btn sm primary" loading={fixing.all}
                  onClick={() => runFix('all', () => fixAllDropins(affected.map(a => a.site_id)), 'All sites')}>
                  <Wrench size={12}/> Fix all {affected.length} sites
                </Button>
              )}
            </div>
            <div style={{padding:'12px 16px'}}>
              {affected.length === 0 ? (
                <div style={{fontSize:12,color:'var(--text3)'}}>
                  Scanned {scan.scanned} site(s) — no orphaned <code>object-cache.php</code> / <code>advanced-cache.php</code> found.
                  {(scan.results || []).flatMap(r => (r.warnings || []).map(w => `${r.site}: ${w.reason}`)).map(line => (
                    <div key={line} style={{marginTop:6}}>{line}</div>
                  ))}
                  {(scan.results || []).some(r => r.supported === false || r.reachable === false) && (
                    <div style={{marginTop:6}}>
                      Skipped: {(scan.results || []).filter(r => r.supported === false || r.reachable === false)
                        .map(r => `${r.site} (${r.error})`).join(' · ')}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div style={{background:'var(--accent-bg)',border:'1px solid var(--accent2)',borderRadius:8,padding:'10px 12px',fontSize:11,color:'var(--accent-text)',marginBottom:12}}>
                    A cache plugin was removed but left its drop-in in <code>wp-content</code>. The file errors on
                    every request, which is why these sites show no plugins to update. Fixing renames the file to a
                    timestamped backup on the site — nothing is deleted permanently.
                  </div>
                  <table className="tbl">
                    <thead><tr><th>Site</th><th>File(s)</th><th>Why</th><th>Fix</th></tr></thead>
                    <tbody>
                      {affected.map(r => (
                        <tr key={r.site_id}>
                          <td><span className="bold">{r.site}</span></td>
                          <td>{(r.broken || []).map(f => <code key={f} style={{fontSize:11,marginRight:6}}>{f}</code>)}</td>
                          <td style={{fontSize:11,color:'var(--text3)',maxWidth:420}}>
                            {(r.reasons || []).join(' ') || r.stray_output}
                          </td>
                          <td>
                            <Button className="btn sm primary" loading={fixing[r.site_id]}
                              onClick={() => runFix(r.site_id, () => fixSiteDropins(r.site_id), r.site)}>
                              <Wrench size={11}/> Fix
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        )}

        {/* Sites list */}
        <div className="card">
          <div className="card-head">
            <h3><Globe size={14}/> Connected sites</h3>
            {/* Column visibility picker — choice persists in this browser */}
            <div style={{ position:'relative' }}>
              <button className="btn sm" onClick={()=>setColsOpen(o=>!o)}>
                <SlidersHorizontal size={12}/> Columns
                {cols.size < ALL_COLS.length && <span className="seg-count">{cols.size}/{ALL_COLS.length}</span>}
                <ChevronDown size={12}/>
              </button>
              {colsOpen && (
                <>
                  <div onClick={()=>setColsOpen(false)} style={{ position:'fixed', inset:0, zIndex:9 }} />
                  <div style={{ position:'absolute', right:0, top:'calc(100% + 6px)', zIndex:10, width:210, background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:12, boxShadow:'var(--shadow)', padding:8 }}>
                    {ALL_COLS.map(c => (
                      <label key={c.key} style={{ display:'flex', alignItems:'center', gap:9, padding:'7px 9px', borderRadius:8, cursor:'pointer' }}>
                        <input type="checkbox" checked={show(c.key)} onChange={()=>toggleCol(c.key)} style={{ accentColor:'var(--accent)' }} />
                        <span style={{ fontSize:12.5, color:'var(--text)' }}>{c.label}</span>
                      </label>
                    ))}
                    <div style={{ fontSize:10.5, color:'var(--text3)', marginTop:6, paddingTop:8, borderTop:'1px solid var(--border)' }}>
                      Site &amp; Actions always stay visible.
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          {sites.length===0 ? (
            <div style={{padding:32,textAlign:'center',color:'var(--text3)',fontSize:12}}>No sites yet.</div>
          ) : (
            <table className="tbl">
              <thead><tr>
                <th>Site</th>
                {show('country')  && <th>Country</th>}
                {show('url')      && <th>URL</th>}
                {show('status')   && <th>Status</th>}
                {show('uptime')   && <th>Uptime (7d)</th>}
                {show('agent')    && <th>Agent</th>}
                {show('wp')       && <th>WordPress</th>}
                {show('php')      && <th>PHP</th>}
                {show('response') && <th>Response</th>}
                <th>Actions</th>
              </tr></thead>
              <tbody>
                {sites.map(s=>(
                  <tr key={s.id}>
                    <td>
                      {editing?.id === s.id
                        ? <input value={editing.name} onChange={e=>setEditing({...editing,name:e.target.value})} style={{background:'var(--bg3)',border:'1px solid var(--accent)',borderRadius:6,padding:'3px 8px',color:'var(--text)',fontSize:12,width:140}}/>
                        : <span className="bold">{s.name}</span>}
                    </td>
                    {show('country') && <td>
                      {editing?.id === s.id
                        ? <select value={editing.country} onChange={e=>setEditing({...editing,country:e.target.value})}
                            style={{background:'var(--bg3)',border:'1px solid var(--accent)',borderRadius:6,padding:'3px 6px',color:'var(--text)',fontSize:12}}>
                            {COUNTRIES.map(c=><option key={c} value={c}>{c}</option>)}
                          </select>
                        : <span style={{display:'inline-flex',alignItems:'center',gap:7}}><Flag country={s.country||'Other'}/> <span style={{fontSize:11,color:'var(--text3)'}}>{s.country||'Other'}</span></span>}
                    </td>}
                    {show('url') && <td><a href={s.url} target="_blank" rel="noreferrer" style={{color:'var(--accent-text)',textDecoration:'none',fontSize:11}}>{s.url}</a></td>}
                    {show('status') && <td>
                      <span className="dot" style={{background:dotColor(s.status),marginRight:5}}></span>
                      <span className={`badge ${s.status==='online'?'green':s.status==='offline'?'red':'amber'}`}>{s.status}</span>
                    </td>}
                    {show('uptime') && <td><UptimeCell stat={uptime.stats[s.id]} series={uptime.series[s.id]} /></td>}
                    {show('agent') && <td><AgentBadge version={s.agent_version} latest={latestAgent} /></td>}
                    {show('wp') && <td>{s.wp_version||'—'}</td>}
                    {show('php') && <td>{s.php_version||'—'}</td>}
                    {show('response') && <td style={{color:s.response_ms<500?'var(--green-text)':'var(--amber-text)'}}>{s.response_ms?`${s.response_ms}ms`:'—'}</td>}
                    <td>
                      <div style={{display:'flex',gap:4}}>
                        {editing?.id === s.id ? (
                          <>
                            <Button className="btn sm success" onClick={handleEditSave}><Check size={11}/></Button>
                            <button className="btn sm" onClick={()=>setEditing(null)}><X size={11}/></button>
                          </>
                        ) : (
                          <>
                            <button className="btn sm" onClick={()=>setEditing({id:s.id,name:s.name,country:s.country||'Other'})}><Pencil size={11}/></button>
                            <Button className="btn sm" onClick={()=>handlePing(s.id)} loading={pinging[s.id]}><RefreshCw size={11}/> {pinging[s.id]?'...':'Ping'}</Button>
                            <Button className="btn sm danger" onClick={()=>handleDelete(s.id)}><Trash2 size={11}/></Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
